import { useState, useEffect } from 'react'
import { groupBy } from 'lodash'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Badge from 'react-bootstrap/Badge'
import Collapse from 'react-bootstrap/Collapse'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faSave, faTimes, faTrash, faLink, faPlus, faMagic } from '@fortawesome/free-solid-svg-icons'
import { confidenceBg } from './confidenceUtils'
import renderDescription from 'shared/render-description'
import renderRuleContent from './render-rule-content'
import ComposeAnalyticModal from './ComposeAnalyticModal'
import AnalyticSearch from './AnalyticSearch'

const AnalyticConfigPreview = ({ analyticId, allAnalytics }) => {
  const analytic = allAnalytics.find((a) => a.id === analyticId)
  if (!analytic?.description) return null

  return (
    <div className="small text-muted mt-1">
      {renderDescription(analytic.description)}
    </div>
  )
}

const PolicyRuleCard = ({
  rule, onUpdate, onDelete, onLinkAnalytic, onUnlinkAnalytic, allAnalytics = [],
  policySectionIds = [], municipalityId, composableSections = [], defaultSectionId
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editCategory, setEditCategory] = useState(rule.category)
  const [editContent, setEditContent] = useState(rule.content)
  const [editSummary, setEditSummary] = useState(rule.summary || '')
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [showCompose, setShowCompose] = useState(false)
  const [editAnalyticId, setEditAnalyticId] = useState(null)

  const canCompose = !!municipalityId && composableSections.length > 0

  const matchedAnalytics = rule.matched_analytics || []
  const linkedAnalyticIds = new Set(matchedAnalytics.map((ma) => ma.analytic_id))

  useEffect(() => {
    setEditCategory(rule.category)
    setEditContent(rule.content)
    setEditSummary(rule.summary || '')
  }, [rule.category, rule.content, rule.summary])

  const handleSave = () => {
    onUpdate({
      ruleId: rule.id,
      data: { category: editCategory, content: editContent, summary: editSummary }
    })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditCategory(rule.category)
    setEditContent(rule.content)
    setEditSummary(rule.summary || '')
    setIsEditing(false)
  }

  const handleLink = (analyticId) => {
    onLinkAnalytic({ analyticId, ruleId: rule.id })
  }

  const handleUnlink = (ma) => {
    onUnlinkAnalytic({ linkId: ma.id })
  }

  return (
    <Card className="mb-2">
      <Card.Header className="py-2 d-flex align-items-center justify-content-between bg-white">
        <div className="d-flex align-items-center">
          {isEditing ? (
            <Form.Control
              type="text"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
              size="sm"
              placeholder="e.g. Spending Approvals"
              style={{ width: '300px' }}
            />
          ) : (
            <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{rule.category}</span>
          )}
        </div>
        <div>
          {isEditing ? (
            <>
              <Button variant="primary" size="sm" className="me-1" onClick={handleSave}>
                <FontAwesomeIcon icon={faSave} />
              </Button>
              <Button variant="light" size="sm" onClick={handleCancel}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </>
          ) : (
            <>
              <Button variant="light" size="sm" className="me-1" onClick={() => setIsEditing(true)}>
                <FontAwesomeIcon icon={faPen} />
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => onDelete(rule.id)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </>
          )}
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        {isEditing ? (
          <Form.Control
            type="text"
            value={editSummary}
            onChange={(e) => setEditSummary(e.target.value)}
            size="sm"
            placeholder="Brief one-line summary"
            className="mb-2"
          />
        ) : rule.summary ? (
          <p className="text-muted small fst-italic mb-2">{rule.summary}</p>
        ) : null}
        {isEditing ? (
          <Form.Control
            as="textarea"
            rows={10}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            size="sm"
            placeholder="Enter the full rule text..."
          />
        ) : (
          <div className="mb-0">{renderRuleContent(rule.content)}</div>
        )}
        <div className="mt-2 pt-2 border-top">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            onClick={() => setShowAnalytics(!showAnalytics)}
            aria-expanded={showAnalytics}
          >
            <FontAwesomeIcon icon={matchedAnalytics.length > 0 ? faLink : faPlus} className="me-1" />
            <small>
              {matchedAnalytics.length > 0
                ? `${matchedAnalytics.length} linked analytic${matchedAnalytics.length !== 1 ? 's' : ''}`
                : 'Link analytics'}
            </small>
            <small className="ms-1">{showAnalytics ? '\u25B2' : '\u25BC'}</small>
          </Button>
          {canCompose && (
            <Button
              variant="light"
              size="sm"
              className="text-primary ms-3 p-0 text-decoration-none"
              onClick={() => setShowCompose(true)}
            >
              <FontAwesomeIcon icon={faMagic} className="me-1" />
              <small>Compose Analytic</small>
            </Button>
          )}
          <Collapse in={showAnalytics}>
            <div className="mt-2">
              {Object.entries(groupBy(matchedAnalytics, (ma) => ma.section_name || 'Unknown')).map(([sectionName, sectionAnalytics]) => (
                <div key={sectionName} className="mb-2">
                  <div
                    className="px-2 py-1 small fw-semibold text-muted"
                    style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef', letterSpacing: '0.04em' }}
                  >
                    {sectionName}
                  </div>
                  {sectionAnalytics.map((ma) => (
                    <div
                      key={ma.analytic_id}
                      className="py-2 px-2"
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <div className="d-flex align-items-start">
                        <div className="flex-grow-1">
                          <small className="fw-bold">{ma.analytic_name}</small>
                          <Badge
                            bg={confidenceBg(ma.confidence)}
                            className="ms-2"
                            style={{ fontSize: '0.65rem' }}
                          >
                            {Math.round(ma.confidence * 100)}%
                          </Badge>
                          {ma.manually_linked && (
                            <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}>manual</Badge>
                          )}
                        </div>
                        {ma.custom && canCompose && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 ms-2 text-primary"
                            onClick={() => setEditAnalyticId(ma.analytic_id)}
                            title="Edit recipe"
                          >
                            <FontAwesomeIcon icon={faPen} style={{ fontSize: '0.7rem' }} />
                          </Button>
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 ms-2 text-danger"
                          onClick={() => handleUnlink(ma)}
                          title="Unlink analytic"
                        >
                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
                        </Button>
                      </div>
                      <AnalyticConfigPreview analyticId={ma.analytic_id} allAnalytics={allAnalytics} />
                    </div>
                  ))}
                </div>
              ))}
              <AnalyticSearch
                allAnalytics={allAnalytics}
                linkedAnalyticIds={linkedAnalyticIds}
                onSelect={handleLink}
                policySectionIds={policySectionIds}
              />
            </div>
          </Collapse>
        </div>
        {showCompose && (
          <ComposeAnalyticModal
            show={showCompose}
            onHide={() => setShowCompose(false)}
            municipalityId={municipalityId}
            sections={composableSections}
            defaultSectionId={defaultSectionId}
            seedDescription={rule.content}
            onSaved={(analytic) => {
              onLinkAnalytic({ analyticId: analytic.id, ruleId: rule.id })
              setShowCompose(false)
            }}
          />
        )}
        {editAnalyticId && (
          <ComposeAnalyticModal
            show={!!editAnalyticId}
            onHide={() => setEditAnalyticId(null)}
            municipalityId={municipalityId}
            analyticId={editAnalyticId}
            sections={composableSections}
            onSaved={() => setEditAnalyticId(null)}
          />
        )}
      </Card.Body>
    </Card>
  )
}

export default PolicyRuleCard
